import React from "react";
import styled from "@emotion/styled";
import { css } from "@emotion/react";

import { useCurrentSession } from "@ats/src/context/CurrentSessionContext";
import { useToastContext } from "@shared/context/ToastContext";
import { useUpdateOrganization } from "@shared/queryHooks/useOrganization";
import { useFeatureGate } from "@shared/hooks/useFeatureGate";
import { PlanFeatures } from "@shared/types/organization";

import SettingsContainer from "@ats/src/components/shared/SettingsContainer";
import UnsavedChangesGuard from "@ats/src/components/shared/UnsavedChangesGuard";
import Button from "@ats/src/components/shared/Button";
import Icon from "@ats/src/components/shared/Icon";
import FormSection from "@ats/src/components/forms/FormSection";
import FormInput from "@ats/src/components/forms/FormInput";
import FormLabel from "@ats/src/components/forms/FormLabel";
import LinkedinIdConfirmationModal from "@ats/src/components/modals/LinkedinIdConfirmationModal";

import { useModalContext } from "@shared/context/ModalContext";
import { INTERNAL_UTM_PARAMS } from "@shared/lib/utils";

const linkedInIneligibilityDetail = (stripeSubscriptionStatus: string | null): string => {
  if (stripeSubscriptionStatus === "trialing") {
    return "LinkedIn requires organizations to be verified. To request access, contact support@polymer.co.";
  }
  if (stripeSubscriptionStatus === null) {
    return "LinkedIn requires a paid plan. If you're on a free trial, contact support@polymer.co to see if you're eligible.";
  }
  return "If you think this is an error, contact support@polymer.co.";
};

function AccountIntegrationsLinkedIn(props) {
  const addToast = useToastContext();
  const { openModal, removeModal } = useModalContext();
  const [isDirty, setIsDirty] = React.useState(false);
  const [errors, setErrors] = React.useState({});
  const { currentOrganization } = useCurrentSession();
  const [companyId, setCompanyId] = React.useState(
    currentOrganization.linkedinCompanyId ? currentOrganization.linkedinCompanyId : "",
  );

  const {
    mutate: updateOrganization,
    isLoading: isLoadingUpdateOrganization,
  } = useUpdateOrganization();

  // Use feature gating instead of hardcoded isLocked
  const { getFeatureInfo } = useFeatureGate();
  const linkedInFeature = getFeatureInfo(PlanFeatures.LINKEDIN_FEED);

  const linkedInIssueRequirements = (currentOrganization.canEnableLinkedin === false ||
    !currentOrganization.ownerEmailIsCorporate) && (
    <Styled.Requirements>
      <FormLabel label="Requirements" />
      <Styled.RequirementInstructions>
        Your organization does not meet the following requirements for LinkedIn integration.
      </Styled.RequirementInstructions>
      <div>
        {currentOrganization.canEnableLinkedin === false ? (
          <Styled.Requirement>
            <Styled.RequirementTitle>
              <Icon name="alert-circle" />
              <span>
                <Styled.RequirementHeadline>LinkedIn is not active.</Styled.RequirementHeadline>{" "}
                {linkedInIneligibilityDetail(currentOrganization.stripeSubscriptionStatus)}
              </span>
            </Styled.RequirementTitle>
          </Styled.Requirement>
        ) : (
          <Styled.Requirement>
            <Styled.RequirementTitle>
              <Icon name="alert-circle" />
              <span>
                <Styled.RequirementHeadline>LinkedIn is not active.</Styled.RequirementHeadline>{" "}
                LinkedIn requires the organization owner to use a company email address (e.g., name@companydomain.com). The owner can update their email in their account settings.
              </span>
            </Styled.RequirementTitle>
            <Styled.RequirementLink href="/account">
              Go to account settings
            </Styled.RequirementLink>
          </Styled.Requirement>
        )}
      </div>
    </Styled.Requirements>
  );

  const linkedInSuccessRequirement = currentOrganization.eligibleForLinkedin &&
    currentOrganization.linkedinCompanyId &&
    currentOrganization.ownerEmailIsCorporate && (
    <Styled.Requirements>
      <FormLabel label="Requirements met" />
      <Styled.RequirementInstructions>
        LinkedIn is fully enabled for your organization. If you're experiencing any issues with job posts syncing, you may need to check your individual job settings.
      </Styled.RequirementInstructions>
      <div>
        <Styled.Requirement>
          <Styled.SuccessRequirementTitle>
            <Icon name="check-circle" />
            LinkedIn is fully enabled for your organization.
          </Styled.SuccessRequirementTitle>
        </Styled.Requirement>
      </div>
    </Styled.Requirements>
  );

  const returnPath = props.match.url;

  // window.logger("%c[AccountIntegrationsLinkedIn] ", "color: #1976D2", {
  //   match: props.match,
  //   location: props.location,
  //   returnPath,
  // });

  window.logger("%c[AccountIntegrationsLinkedIn] render", "color: #1976D2", {
    props,
    companyId,
    errors,
  });

  const onChangeCompanyId = (name, value) => {
    // window.logger(
    //   "%c[AccountIntegrationsLinkedIn] state change",
    //   "color: #1976D2",
    //   value,
    //   companyId
    // );
    setErrors({});
    setIsDirty(true);
    setCompanyId(value);
  };

  const onClickSubmit = async (e) => {
    e.preventDefault();

    const modal = (
      <LinkedinIdConfirmationModal
        onConfirm={handleSubmit}
        onCancel={removeModal}
        linkedInCompanyId={companyId}
      />
    );

    if (companyId === "") {
      handleSubmit();
    } else {
      openModal(modal);
    }
  };

  const handleSubmit = async () => {
    updateOrganization(
      { id: currentOrganization.id, linkedinCompanyId: companyId },
      {
        onSuccess: () => {
          setIsDirty(false);
          addToast({
            title: `Successfully updated`,
            kind: "success",
          });
          removeModal();
        },
        onError: (response: any) => {
          window.logger("%c[AccountIntegrationsLinkedIn] Error OnSubmit", "color: #1976D2", {
            response,
            message: response.data?.errors,
          });
          const message = response.data?.errors?.general
            ? response.data.errors.general[0]
            : response.data?.errors?.linkedinCompanyId
            ? `LinkedIn company id ${response.data.errors.linkedinCompanyId[0]}`
            : "Something went wrong. Please try again or contact support@polymer.co.";
          addToast({
            title: message,
            kind: "warning",
            delay: 10000,
          });
        },
      },
    );
  };

  const BottomBar = (
    <>
      <UnsavedChangesGuard
        hasUnsavedChanges={isDirty}
        navigate={(pathname) => {
          window.logger(
            "%c[AccountIntegrationsLinkedIn] UnsavedChangesGuard navigate",
            "color: #1976D2",
            pathname,
          );
          setIsDirty(false);
          props.history.push(pathname);
        }}
      />

      <Button
        className="submit-button"
        type="button"
        onClick={onClickSubmit}
        loading={isLoadingUpdateOrganization}
        aria-label="Save changes"
      >
        Save changes
      </Button>
    </>
  );

  const description = (
    <>
      For help finding your LinkedIn Company ID{"  "}
      <a
        href="https://www.linkedin.com/help/linkedin/answer/a415420"
        target="_blank"
        rel="noreferrer noopener"
      >
        view the LinkedIn help docs.
      </a>
    </>
  );

  const ViewLink = (
    <>
      <Button
        type="externalLink"
        link={`https://help.polymer.co/en/articles/8828635-post-your-jobs-to-linkedin${INTERNAL_UTM_PARAMS}`}
        styleType="text"
      >
        View docs
        <Icon name="external-link" />
      </Button>
    </>
  );

  return (
    <SettingsContainer
      title="LinkedIn Limited Listings"
      description="Publish jobs to LinkedIn for free using LinkedIn Limited Listings. Just add your LinkedIn company id below to opt in."
      bottomBar={BottomBar}
      fullWidthForm={true}
      actions={ViewLink}
      locked={linkedInFeature.isLocked}
      upgradePlanKey={linkedInFeature.upgradePlanKey}
      planDisplayName={linkedInFeature.planDisplayName}
    >
      {!linkedInFeature.isLocked && (
        <>
          {linkedInIssueRequirements}
          {linkedInSuccessRequirement}
        </>
      )}

      <FormSection>
        <FormInput
          name="companyId"
          onChange={onChangeCompanyId}
          label="LinkedIn Company ID"
          description={description}
          value={companyId}
          errors={errors}
          inputType="number"
          isDisabled={linkedInFeature.isLocked}
        />
      </FormSection>
    </SettingsContainer>
  );
}

export default AccountIntegrationsLinkedIn;

/* Styled Components
======================================================= */
let Styled: any;
Styled = {};

Styled.Requirements = styled.div((props) => {
  const t: any = props.theme;
  return css`
    label: AccountIntegrationsLinkedIn_Requirements;
    ${[t.mt(6), t.mb(8)]}
    display: flex;
    flex-direction: column;
  `;
});

Styled.RequirementInstructions = styled.p((props) => {
  const t: any = props.theme;
  return css`
    label: AccountIntegrationsLinkedIn_RequirementInstructions;
    ${[t.text.xs, t.mt(-1), t.mb(2)]};
    color: ${t.dark ? t.color.gray[400] : t.color.gray[600]};
    line-height: 1.4;
    max-width: 32rem;
  `;
});

Styled.Requirement = styled.div((props) => {
  const t: any = props.theme;
  return css`
    label: AccountIntegrationsLinkedIn_Requirement;
    ${[t.px(4)]}
    min-height: ${t.spacing[12]};
    border: 1px solid ${t.dark ? t.color.gray[800] : t.color.gray[200]};
    background-color: ${t.dark ? t.color.gray[800] : "transparent"};
    border-top: 1px solid ${t.dark ? t.color.gray[700] : "transparent"};
    display: flex;
    justify-content: space-between;
    align-items: center;

    &:first-of-type {
      border-top: 1px solid ${t.dark ? t.color.gray[800] : t.color.gray[200]};
      border-top-left-radius: 0.4375rem;
      border-top-right-radius: 0.4375rem;
    }

    &:last-of-type {
      border-bottom-left-radius: 0.4375rem;
      border-bottom-right-radius: 0.4375rem;
    }
  `;
});

Styled.RequirementTitle = styled.div((props) => {
  const t: any = props.theme;
  return css`
    label: AccountIntegrationsLinkedIn_RequirementTitle;
    ${[t.my(2), t.mr(8)]};
    color: ${t.dark ? t.color.gray[400] : t.color.gray[600]};
    display: flex;
    align-items: center;
    line-height: 1.4;


    svg {
      ${[t.mr(2)]};
      color: ${t.dark ? t.color.red[500] : t.color.red[600]};
      flex-shrink: 0;
    }
  `;
});

Styled.SuccessRequirementTitle = styled.div((props) => {
  const t: any = props.theme;
  return css`
    label: AccountIntegrationsLinkedIn_SuccessRequirementTitle;
    ${[t.my(2), t.mr(8)]};
    color: ${t.dark ? "#4ADE80" : "#16A34A"};
    display: flex;
    align-items: center;
    line-height: 1.4;

    svg {
      ${[t.mr(2)]};
      color: ${t.dark ? "#4ADE80" : "#16A34A"};
      flex-shrink: 0;
    }
  `;
});

Styled.RequirementHeadline = styled.span((props) => {
  const t: any = props.theme;
  return css`
    label: AccountIntegrationsLinkedIn_RequirementHeadline;
    color: ${t.dark ? t.color.red[500] : t.color.red[600]};
  `;
});

Styled.RequirementLink = styled.a((props) => {
  const t: any = props.theme;
  return css`
    label: AccountIntegrationsLinkedIn_RequirementLink;
    ${[t.text.sm, t.text.bold]};
    color: ${t.dark ? t.color.gray[400] : t.color.gray[600]};
    cursor: pointer;
    text-decoration: none;
    white-space: nowrap;

    &:hover {
      text-decoration: underline;
    }
    &:visited {
      color: ${t.dark ? t.color.gray[400] : t.color.gray[600]};
    }
  `;
});
